/* eslint-disable @angular-eslint/prefer-inject */
import { ProjectDataService } from '../infrastructure/projects/project.data.service';
import {
    ProjectCreate,
    ProjectDto,
    ProjectRead,
    ProjectSearchParams,
} from '../entities/project.dto';
import { ProjectStore } from '../infrastructure/projects/project.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { ProjectMapper } from '../infrastructure/projects/project.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { ProjectStatus, ResourceAuth } from '@control-tower/platform-wms';
import { filter, map, Observable, shareReplay, switchMap } from 'rxjs';
import {
    IQueryParams,
    PFWmsEntities,
    ProfileProvider,
} from '@pf/shared-common';
import { WarehouseFacade } from './warehouse.facade';
import { isValue } from '@pf/shared-utility';
import { WarehouseDto } from '../entities/warehouse.dto';

@Injectable()
export class ProjectFacade extends AbstractManageEntityFacade<
    ProjectDto,
    ProjectRead,
    ProjectCreate,
    ProjectSearchParams
> {
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly profileProvider = inject(ProfileProvider);
    nameField: keyof ProjectDto = 'number';

    constructor(
        dataService: ProjectDataService,
        store: ProjectStore,
        mapper: ProjectMapper
    ) {
        super(dataService, null, store, mapper);

        this.listenForRealTimeEvents(PFWmsEntities.Project, {
            processEventsIfNotInStore: true,
        });
    }

    override getDefaultSearchParams$(
        params: IQueryParams<ProjectDto>
    ): Observable<IQueryParams<ProjectDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: ProjectCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ProjectDto, string>> {
        return { number: searchText };
    }

    pendingProjects$() {
        return this.loadProjects$(ProjectStatus.Pending, false);
    }

    pickedProjects$() {
        return this.loadProjects$(ProjectStatus.Picked, false);
    }

    private loadProjects$(status: ProjectStatus, currentUserOnly: boolean) {
        return this.searchAll$(
            {
                pageIndex: 1,
                pageSize: 50,
                filters: {
                    status,
                    assignedToUser: currentUserOnly
                        ? this.profileProvider.profile.userId
                        : undefined,
                },
            },
            false
        ).pipe(
            map(() => true),
            switchMap(() => this.observeProjects$(status, currentUserOnly)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private observeProjects$(status: ProjectStatus, currentUserOnly: boolean) {
        return this.warehouseFacade.active$.pipe(
            filter(isValue),
            switchMap((warehouse: WarehouseDto) => {
                return this.store.filter$((project: ProjectRead) => {
                    return (
                        !project.isDeleted &&
                        project.warehouse?.id === warehouse.id &&
                        project.status === status &&
                        (currentUserOnly
                            ? project.assignedToUser?.id ===
                              this.profileProvider.profile.userId
                            : true)
                    );
                });
            }),
            map((projects: ProjectRead[]) => this.mapper.toDTOList(projects))
        );
    }
}
